import { z } from 'zod';

import { ConcreteCardAcquirerSchema, ConcreteCardBrandSchema } from './card-fee-rates.schemas.js';

const CentsSchema = z.string().regex(/^\d+$/, 'must be a non-negative integer string (cents)');

/** Place-scoped cash station id (placeId lives in shared PlaceIdParamsSchema). */
export const CashStationIdParamsSchema = z.object({
  placeId: z.uuid(),
  cashStationId: z.uuid(),
});
export type CashStationIdParams = z.infer<typeof CashStationIdParamsSchema>;

export const CaixaGatewayChargeIdParamsSchema = z.object({
  chargeId: z.uuid(),
});
export type CaixaGatewayChargeIdParams = z.infer<typeof CaixaGatewayChargeIdParamsSchema>;

export const CashStationResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  name: z.string(),
  defaultTefTerminalId: z.string().nullable(),
  isArchived: z.boolean(),
});
export type CashStationResponse = z.infer<typeof CashStationResponseSchema>;

export const ListCashStationsQuerySchema = z.object({
  includeArchived: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type ListCashStationsQuery = z.infer<typeof ListCashStationsQuerySchema>;

export const ListCashStationsResponseSchema = z.object({
  items: z.array(CashStationResponseSchema),
});
export type ListCashStationsResponse = z.infer<typeof ListCashStationsResponseSchema>;

export const CreateCashStationBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  defaultTefTerminalId: z.string().trim().min(1).max(120).nullable().optional(),
});
export type CreateCashStationBody = z.infer<typeof CreateCashStationBodySchema>;

export const UpdateCashStationBodySchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  defaultTefTerminalId: z.string().trim().min(1).max(120).nullable().optional(),
  archive: z.boolean().optional(),
});
export type UpdateCashStationBody = z.infer<typeof UpdateCashStationBodySchema>;

export const ListTefTerminalsQuerySchema = z.object({});
export type ListTefTerminalsQuery = z.infer<typeof ListTefTerminalsQuerySchema>;

export const ListTefTerminalsResponseSchema = z.object({
  items: z.array(
    z.object({
      providerTerminalId: z.string(),
      label: z.string().nullable(),
      status: z.string(),
    }),
  ),
});
export type ListTefTerminalsResponse = z.infer<typeof ListTefTerminalsResponseSchema>;

export const CaixaTefMethodSchema = z.enum(['CREDIT_CARD', 'DEBIT_CARD', 'PIX']);
export type CaixaTefMethod = z.infer<typeof CaixaTefMethodSchema>;

export const StartCaixaTefChargeBodySchema = z
  .object({
    placeId: z.uuid(),
    serviceOrderId: z.uuid(),
    amountCents: CentsSchema,
    method: CaixaTefMethodSchema,
    cashStationId: z.uuid().nullable().optional(),
    providerTerminalId: z.string().trim().min(1).max(120).nullable().optional(),
    idempotencyKey: z.string().trim().min(8).max(120),
    cardBrand: ConcreteCardBrandSchema.optional(),
    acquirer: ConcreteCardAcquirerSchema.optional(),
    installmentCount: z.number().int().positive().optional(),
  })
  .superRefine((body, ctx) => {
    const isCard = body.method === 'CREDIT_CARD' || body.method === 'DEBIT_CARD';
    if (!isCard) {
      if (
        body.cardBrand !== undefined ||
        body.acquirer !== undefined ||
        body.installmentCount !== undefined
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'card context is only allowed for credit and debit',
          path: ['cardBrand'],
        });
      }
      return;
    }
    if (
      body.cardBrand === undefined ||
      body.acquirer === undefined ||
      body.installmentCount === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'card brand, acquirer and installment count are required',
        path: ['cardBrand'],
      });
      return;
    }
    if (body.method === 'DEBIT_CARD' && body.installmentCount !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'debit accepts a single installment',
        path: ['installmentCount'],
      });
    }
  });
export type StartCaixaTefChargeBody = z.infer<typeof StartCaixaTefChargeBodySchema>;

export const StartCaixaTefChargeResponseSchema = z.object({
  chargeId: z.uuid(),
  status: z.enum(['PENDING', 'PAID', 'FAILED', 'CANCELLED']),
  attemptCount: z.number().int().positive(),
  providerOrderId: z.string().nullable(),
  providerTerminalId: z.string().nullable(),
  channel: z.literal('TEF'),
  method: CaixaTefMethodSchema,
  amountCents: CentsSchema,
});
export type StartCaixaTefChargeResponse = z.infer<typeof StartCaixaTefChargeResponseSchema>;

export const RetryCaixaTefChargeResponseSchema = z.object({
  chargeId: z.uuid(),
  status: z.string(),
  attemptCount: z.number().int().positive(),
  providerOrderId: z.string().nullable(),
  canRetry: z.boolean(),
});
export type RetryCaixaTefChargeResponse = z.infer<typeof RetryCaixaTefChargeResponseSchema>;

export const StartCaixaPixDynamicChargeBodySchema = z.object({
  placeId: z.uuid(),
  serviceOrderId: z.uuid(),
  amountCents: CentsSchema,
  cashStationId: z.uuid().nullable().optional(),
  idempotencyKey: z.string().trim().min(8).max(120),
  debtorName: z.string().trim().min(1).max(120).nullable().optional(),
});
export type StartCaixaPixDynamicChargeBody = z.infer<typeof StartCaixaPixDynamicChargeBodySchema>;

export const StartCaixaPixDynamicChargeResponseSchema = z.object({
  chargeId: z.uuid(),
  status: z.enum(['PENDING', 'PAID', 'FAILED', 'CANCELLED']),
  channel: z.literal('PIX_DYNAMIC'),
  method: z.literal('PIX'),
  amountCents: CentsSchema,
  pixCopiaECola: z.string().nullable(),
  pixLocation: z.string().nullable(),
  providerOrderId: z.string().nullable(),
});
export type StartCaixaPixDynamicChargeResponse = z.infer<
  typeof StartCaixaPixDynamicChargeResponseSchema
>;

export const CaixaGatewayChargeResponseSchema = z.object({
  chargeId: z.uuid(),
  placeId: z.uuid(),
  serviceOrderId: z.uuid(),
  cashStationId: z.uuid().nullable(),
  channel: z.enum(['TEF', 'PIX_DYNAMIC', 'PIX_STATIC']),
  method: z.enum(['CREDIT_CARD', 'DEBIT_CARD', 'PIX']),
  status: z.enum(['PENDING', 'PAID', 'FAILED', 'CANCELLED']),
  amountCents: CentsSchema,
  attemptCount: z.number().int().positive(),
  canRetry: z.boolean(),
  providerOrderId: z.string().nullable(),
  providerTerminalId: z.string().nullable(),
  pixCopiaECola: z.string().nullable(),
  pixLocation: z.string().nullable(),
  cardBrand: ConcreteCardBrandSchema.nullable(),
  cardAcquirer: ConcreteCardAcquirerSchema.nullable(),
  cardInstallmentCount: z.number().int().nullable(),
  paymentId: z.string().nullable(),
  lastError: z.string().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type CaixaGatewayChargeResponse = z.infer<typeof CaixaGatewayChargeResponseSchema>;

export const RetrySmartTefProvisionResponseSchema = z.object({
  placeId: z.uuid(),
  status: z.enum(['PENDING', 'ACTIVE', 'FAILED']),
  providerStoreId: z.string().nullable(),
  lastError: z.string().nullable(),
});
export type RetrySmartTefProvisionResponse = z.infer<typeof RetrySmartTefProvisionResponseSchema>;

/** Read model for place Smart TEF store provision (includes NONE when never attempted). */
export const SmartTefStoreProvisionResponseSchema = z.object({
  placeId: z.uuid(),
  status: z.enum(['NONE', 'PENDING', 'ACTIVE', 'FAILED']),
  providerStoreId: z.string().nullable(),
  lastError: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type SmartTefStoreProvisionResponse = z.infer<typeof SmartTefStoreProvisionResponseSchema>;

export const SyncSmartTefPortalUserBodySchema = z.object({
  password: z.string().min(8).max(128),
});
export type SyncSmartTefPortalUserBody = z.infer<typeof SyncSmartTefPortalUserBodySchema>;

export const SmartTefPortalUserResponseSchema = z.object({
  placeId: z.uuid(),
  email: z.string().nullable(),
  syncStatus: z.enum(['PENDING', 'SYNCED', 'FAILED', 'NONE']),
  lastError: z.string().nullable(),
  syncedAt: z.iso.datetime().nullable(),
});
export type SmartTefPortalUserResponse = z.infer<typeof SmartTefPortalUserResponseSchema>;

export const SmartTefWebhookBodySchema = z.object({
  tenantId: z.uuid(),
  providerOrderId: z.string().trim().min(1),
  status: z.string().trim().min(1),
  providerSnapshot: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type SmartTefWebhookBody = z.infer<typeof SmartTefWebhookBodySchema>;

export const SmartTefWebhookResponseSchema = z.object({
  chargeId: z.uuid(),
  status: z.string(),
  paymentId: z.string().nullable(),
  processed: z.boolean(),
});
export type SmartTefWebhookResponse = z.infer<typeof SmartTefWebhookResponseSchema>;

export const SmartTefStoreWebhookResponseSchema = z.object({
  placeId: z.uuid(),
  webhookStatus: z.enum(['NONE', 'PENDING', 'ACTIVE', 'FAILED']),
  lastError: z.string().nullable(),
  configuredAt: z.iso.datetime().nullable(),
});
export type SmartTefStoreWebhookResponse = z.infer<typeof SmartTefStoreWebhookResponseSchema>;

export const StartCaixaPixStaticChargeBodySchema = z.object({
  placeId: z.uuid(),
  serviceOrderId: z.uuid(),
  amountCents: CentsSchema,
  cashStationId: z.uuid().nullable().optional(),
  idempotencyKey: z.string().trim().min(8).max(120),
});
export type StartCaixaPixStaticChargeBody = z.infer<typeof StartCaixaPixStaticChargeBodySchema>;

export const StartCaixaPixStaticChargeResponseSchema = z.object({
  chargeId: z.uuid(),
  status: z.enum(['PENDING', 'PAID', 'FAILED', 'CANCELLED']),
  channel: z.literal('PIX_STATIC'),
  method: z.literal('PIX'),
  amountCents: CentsSchema,
  pixCopiaECola: z.string().nullable(),
  expiresAt: z.iso.datetime().nullable(),
});
export type StartCaixaPixStaticChargeResponse = z.infer<
  typeof StartCaixaPixStaticChargeResponseSchema
>;

export const ConfirmCaixaPixStaticChargeResponseSchema = z.object({
  chargeId: z.uuid(),
  status: z.string(),
  paymentId: z.string().nullable(),
  processed: z.boolean(),
});
export type ConfirmCaixaPixStaticChargeResponse = z.infer<
  typeof ConfirmCaixaPixStaticChargeResponseSchema
>;

export const CancelCaixaPixStaticChargeResponseSchema = z.object({
  chargeId: z.uuid(),
  status: z.string(),
});
export type CancelCaixaPixStaticChargeResponse = z.infer<
  typeof CancelCaixaPixStaticChargeResponseSchema
>;

export const CaixaPixCapabilityResponseSchema = z.object({
  placeId: z.uuid(),
  mode: z.enum(['NONE', 'STATIC', 'DYNAMIC']),
  openFinanceActive: z.boolean(),
});
export type CaixaPixCapabilityResponse = z.infer<typeof CaixaPixCapabilityResponseSchema>;
